import { useEffect, useRef, useState } from 'react';

/**
 * 스켈레톤 표시 지연 + 최소 노출 시간 제어
 * - 로딩 시작 후 delay(200ms) 동안 아무것도 표시하지 않음
 * - delay 이내에 로딩 완료 시 스켈레톤 없이 바로 콘텐츠 표시
 * - delay 이후에도 로딩 중이면 스켈레톤 표시, 최소 minDuration(400ms) 유지
 */
export function useDelayedSkeleton(
  isLoading: boolean,
  delay = 200,
  minDuration = 400,
) {
  const [phase, setPhase] = useState<'idle' | 'skeleton' | 'content'>(
    isLoading ? 'idle' : 'content',
  );
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const skeletonShownAtRef = useRef(0);

  useEffect(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    if (isLoading) {
      setPhase('idle');

      const timer = setTimeout(() => {
        skeletonShownAtRef.current = Date.now();
        setPhase('skeleton');
      }, delay);
      timersRef.current.push(timer);
    } else {
      if (phase === 'skeleton') {
        // 스켈레톤이 보인 상태 → 최소 유지 시간 보장
        const elapsed = Date.now() - skeletonShownAtRef.current;
        const remaining = Math.max(0, minDuration - elapsed);

        if (remaining > 0) {
          const timer = setTimeout(() => setPhase('content'), remaining);
          timersRef.current.push(timer);
        } else {
          setPhase('content');
        }
      } else {
        // idle 상태 (빠른 응답) → 바로 콘텐츠
        setPhase('content');
      }
    }

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [isLoading, delay, minDuration]);

  return {
    showSkeleton: phase === 'skeleton',
    showContent: phase === 'content',
  };
}
